import React, { useState, useEffect } from "react";
import { isMobile } from 'react-device-detect';
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";
import { useMsal } from "@azure/msal-react";
import SkillDivider from "../components/skillpath/SkillDivider";
import { ISkillPath } from "../model/SkillPath.model";
import { ISkillLevel } from "../model/SkillLevel.model";
import LevelIcon from "../components/skillpath/LevelIcon";
import useWindowDimensions from "../hooks/windowDimensions";
import CompetencyPeriodSelector from "../components/CompetencyPeriodSelector/CompetencyPeriodSelector";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/Accordion/Accordion";
import parse from 'html-react-parser';
import '../styles/pages/EmployeeProgression.scss';
import '../styles/pages/MyCompetencies.scss';
import { Icon } from "../components/icons/icons";
import { IEmployeeProgression } from "../model/EmployeeProgression.model";
import DesktopCompetencyGrid from "../components/skillpath/DesktopCompetencyGrid";
import EmployeeProgressionOptions from "../components/skillpath/EmployeeProgressionOptions";
import SkillLevelCheckboxes from "../components/skillpath/SkillLevelCheckboxes";
import Loading from "../components/Loading/Loading";

const MyCompetencies = () => {
  const [isMobileClient, setIsMobileClient] = React.useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const { width } = useWindowDimensions();
  const { instance } = useMsal();
  const [account] = useState(instance.getActiveAccount());
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiCFSkillPath.scopes.read,
  });

  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [skillTree, setSkillTree] = useState<ISkillPath[]>([]);
  const [skillTreeLoaded, setSkillTreeLoaded] = useState<boolean>(false);
  const [progressions, setProgressions] = useState<IEmployeeProgression[]>([]);
  const [progressionLevels, setProgressionLevels] = useState<any[]>([]);

  useEffect(() => {
    setIsMobileClient(isMobile || width < 1100);
  }, [width]);

  // Load bookmarks and mastered levels once (not period-dependent)
  useEffect(() => {
    if (!account) return;
    setLoading(true);
    execute("GET", "/api/EmpProgs", undefined).then((response: any) => {
      if (!response) return;
      setProgressions(response.progressions ?? []);
      setProgressionLevels(response.levels ?? []);
    })
    .finally(()=> setLoading(false));
  }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load skill tree when period changes
  useEffect(() => {
    if (!account || !selectedPeriodId || skillTreeLoaded) return;
    const upn = account.username;
    setLoading(true);
    execute("GET", `/api/EmpCompetency/skillTree/${encodeURIComponent(upn)}/${selectedPeriodId}`, undefined)
      .then((response: any) => {
        if (!response) return;
        setSkillTree(flattenSkillTree(response.skillPaths ?? []));
        setSkillTreeLoaded(true);
      })
      .finally(()=> setLoading(false));
  }, [account, selectedPeriodId, skillTreeLoaded, execute]);

  // Flatten so each entry in the array is exactly one skillPath → one skillGroup → one skill,
  // matching the rendering used by the EmployeeProgression page.
  const flattenSkillTree = (skillPaths: any[]): ISkillPath[] => {
    const flat: ISkillPath[] = [];
    skillPaths.forEach(sp => {
      sp.skillGroups?.forEach((sg: any) => {
        sg.skills?.forEach((s: any) => {
          const sp1: any = JSON.parse(JSON.stringify(sp));
          const sg1: any = JSON.parse(JSON.stringify(sg));
          sp1.skillGroups = [sg1];
          sg1.skills = [s];
          flat.push(sp1);
        });
      });
    });
    return flat;
  };

  const getDividerText = (pathName: string, groupName: string, skillName: string) =>
    `${pathName} : ${groupName} : ${skillName}`;

  const getSkillDetails = (skill: any, level: ISkillLevel) => {
    if (!Array.isArray(skill.details)) return null;
    return skill.details
      .filter((sd: any) => sd.skillLevelId === level.skillLevelId)
      .map((detail: any, i: number) => (
        <div key={i} className='m-t--75 m-b--75'>
          {parse((detail.skillText + "").replaceAll('<a href=', '<a target="_blank" rel="noopener noreferrer" href='))}
        </div>
      ));
  };

  const getAccordionColor = (skillPathId: any) => {
    switch (skillPathId) {
      case 1: return "secondary";
      case 2: return "orange";
      case 3: return "purple";
      case 4: return "yellow";
      case 5: return "green";
      default: return "orange";
    }
  };
   const getUL = () => {
      return (
        <>
          <h4 className="fnt-wght-bold m-b--50 m-t-0">Understanding Your Competencies</h4>
          <ul className="reset">
            <li className="ep-bookmark-li">
              <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
              <p className="m-t--50 m-b--50">These are the competencies you are evaluated on in your performance review.</p>
            </li>
            <li className="ep-bookmark-li">
              <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
              <p className="m-t--50 m-b--50">Speak with your manager about opportunities to develop these skills.</p>
            </li>
            <li className="ep-bookmark-li">
              <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
              <p className="m-t--50 m-b--50">As you master skills, you and your manager may discuss replacing competencies with more relevant ones.</p>
            </li>
            <li className="ep-bookmark-li">
              <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
              <p className="m-t--50 m-b--50">Any competency changes must be finalized by the end of the competency management period to allow time to align expectations for the upcoming review year.</p>
            </li>
          </ul>
          <h4 className="fnt-wght-bold m-b--50">Tip</h4>
          <p className="m-t--50 m-b--50">💡 Remember: Job Proficiency refers to the technical skills and knowledge within your discipline-specific career framework.</p>
        </>
      )
    }

  if (isMobileClient) {
    return (
      <div className="mobile">
        <section className='bg-primary p-2 clr-white'>
          <div className="dis-grid gap-1">
            <div>
              <h2 className="fnt-wght-bold m-b-1 m-t-0 h1-size">My Competencies{account?.name ? `: ${account.name}` : ''}</h2>
              
            </div>
            <CompetencyPeriodSelector onPeriodChange={(p) => { setSelectedPeriodId(p.competencyMgmtPeriodId); setSkillTree([]); setSkillTreeLoaded(false); }} />
          </div>
          <div className="m-t-2-5">
            {getUL()}
          </div>
        </section>
        {skillTree.map((skillPath: any, spIdx: number) => (
          skillPath.skillGroups?.map((sg: any, sgIdx: number) => (
            sg.skills?.map((skill: any) => (
              <Accordion key={`${skill.SkillId}-${spIdx}-${sgIdx}`}>
                <AccordionItem id={skill.SkillId} key={skill.SkillId}>
                  <AccordionTrigger color={getAccordionColor(skillPath.skillPathId)} headingLevel={3}>
                    {getDividerText(skillPath.skillPathName, sg.skillGroupName, skill.skillName)}
                  </AccordionTrigger>
                  <AccordionContent color={getAccordionColor(skillPath.skillPathId)}>
                    <div className="core-desktop-max-width">
                      <div className="m-b-1 dis-flex dis-flex-rows dis-flex-aln-center p-t-1 gap--25">
                        <EmployeeProgressionOptions
                          source="competencies"
                          progression={progressions.find((p: IEmployeeProgression) => p.skillId === skill.SkillId)}
                          skill={skill}
                          skillLevels={skillPath.skillLevels ?? []}
                          isManager={false}
                          bookmarkAdded={(bm) => setProgressions(prev => [...prev, bm])}
                          bookmarkRemoved={(bm) => setProgressions(prev => prev.filter(p => p.employeeProgressionId !== bm.employeeProgressionId))}
                        />
                        <div className="gap--25 p--50 bdr-radius--25 bg-white no-border squared-layout mc-not-button">
                          <Icon name="icon-bulb-circle" className="icon icon-primary"></Icon>
                          <span className="clr-primary fnt-wght-500 aln-center">Competency Skill</span>
                        </div>
                      </div>
                      {skillPath.skillLevels?.map((level: ISkillLevel) => (
                        <div key={level.skillLevelId} className="mobile-skill-levels-container">
                          <h3 className="m-t-1-5 m-b-50">{level.skillLevelName}</h3>
                          <div className='m-t--75'>
                            {getSkillDetails(skill, level)}
                          </div>
                          <SkillLevelCheckboxes
                            level={level}
                            skill={skill}
                            skillLevels={skillPath.skillLevels ?? []}
                            isManager={false}
                            currentUpn={undefined}
                            employeeProgressionLevels={progressionLevels}
                            employeeProgressionLevelsLength={progressionLevels.length}
                            allReadonly={true}
                          />
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))
          ))
        ))}
      <Loading isLoading={loading} />
      </div>
      
    );
  }

  return (
    <>
      <div className="core-container desktop">
        <div className="core-desktop-max-width padded">
          <div className="ep-desktop-heading-ul-container">
            <div>
              <h3 className="fnt-wght-bold m-b-1 m-t-0 h1-size">My Competencies{account?.name ? `: ${account.name}` : ''}</h3>
               <CompetencyPeriodSelector onPeriodChange={(p) => { setSelectedPeriodId(p.competencyMgmtPeriodId); setSkillTree([]); setSkillTreeLoaded(false); }} />
            </div>
            <div className="m-t-2-5">
              {getUL()}
            </div>
          </div>
        </div>
      </div>

      <div className='core-desktop-max-width bottom p-t-1-75 p-b-1-5 sticky-top bg-white'>
        <div>&nbsp;</div>
        <div className='aln-center'>
          <LevelIcon
            iconName='icon-novice'
            iconClassNames={['icon-primary', 'level-icons', 'm-b--75']}
            levelText='Level 1 - Novice'
            levelTextClassNames={['fnt-wght-900', 'fnt-sz-med-lrg', 'm-b--50']}
            detailText=''
            detailTextClassNames={['fnt-wght-900', 'fnt-sz-sml-med']}
            iconColor="#0b254d"
          />
        </div>
        <div className='aln-center'>
          <LevelIcon
            iconName='icon-mid-level'
            iconClassNames={['icon-primary', 'level-icons', 'm-b--75']}
            levelText='Level 2 - Knowledge Competent / Professional'
            levelTextClassNames={['fnt-wght-900', 'fnt-sz-med-lrg', 'm-b--50']}
            detailText=''
            detailTextClassNames={['fnt-wght-900', 'fnt-sz-sml-med']}
            iconColor="#0b254d"
          />
        </div>
        <div className='aln-center'>
          <LevelIcon
            iconName='icon-advanced'
            iconClassNames={['icon-primary', 'level-icons', 'm-b--75']}
            levelText='Level 3 - Advanced Mentor'
            levelTextClassNames={['fnt-wght-900', 'fnt-sz-med-lrg', 'm-b--50']}
            detailText=''
            detailTextClassNames={['fnt-wght-900', 'fnt-sz-sml-med']}
            iconColor="#0b254d"
          />
        </div>
      </div>
      <Loading isLoading={loading} />
      {skillTreeLoaded && skillTree.length === 0 && (
        <div className="core-desktop-max-width padded">
          <p>No competencies assigned for this period.</p>
        </div>
      )}

      {skillTree.map((skillPath: any, spIdx: number) => (
        skillPath.skillGroups?.map((sg: any, sgIdx: number) => (
          sg.skills?.map((skill: any) => (
            <React.Fragment key={`${skill.SkillId}-${spIdx}-${sgIdx}`}>
              <SkillDivider
                dividerClassNames={["p-t--50", "p-b--50", "skill-divider-container"]}
                headingLevel={4}
                skillLevels={skillPath.skillLevels as ISkillLevel[]}
                headingClassNames={["clr-white", "fnt-wght-500", "m-0"]}
                dividerText={getDividerText(skillPath.skillPathName, sg.skillGroupName, skill.skillName)}
                tooltipContent={skill.skillDescription}
                backgroundColor={skillPath.highlightColor}
                skillPathId={sg.skillPathId}
                isBookmarksSkillDivider={true}
              />
              <DesktopCompetencyGrid
                skill={skill}
                skillLevels={skillPath.skillLevels ?? []}
                borderColor={skillPath.highlightColor}
                progression={progressions.find((p: IEmployeeProgression) => p.skillId === skill.SkillId)}
                employeeProgressionLevels={progressionLevels}
                bookmarkAdded={(bm) => setProgressions(prev => [...prev, bm])}
                bookmarkRemoved={(bm) => setProgressions(prev => prev.filter(p => p.employeeProgressionId !== bm.employeeProgressionId))}
              />
            </React.Fragment>
          ))
        ))
      ))}
    </>
  );
};

export default MyCompetencies;
